/**
 * Bstory — <bstory-size-label>
 *
 * Keeps the Main Product size pill in sync with the selected variant.
 * Liquid renders the correct mapped value on first paint; this element only
 * handles subsequent changes.
 *
 * SOURCE OF TRUTH: the product form's own variant-id input.
 *
 * `VariantPicker.selectVariant()` (assets/global.min.js) does, in order:
 *
 *     this.masterInput.value = this.currentVariant.id;
 *     this.masterInput.dispatchEvent(new CustomEvent("change", { bubbles: true, ... }));
 *     this.masterInput.form.dispatchEvent(new CustomEvent(variantChange, ...));
 *
 * and the `idELForm` getter shows `masterInput` is the form's hidden
 * `input[name="id"]`. So after any selection the form always holds the correct
 * variant id, and a native bubbling `change` always reaches the form.
 *
 * This element therefore reads the id straight off the form instead of trusting
 * an event payload — no event name, no `detail.variant`, no options array and
 * no index arithmetic at runtime. Everything else it needs (the mappings and a
 * minimal variant list) is rendered beside it as JSON by the block.
 *
 * Scoping is inherent, not compared: the element only ever reads the form named
 * by its own `data-form-id`, so the sticky bar, Quick View, Featured Product
 * and any second Main Product instance can never drive it.
 *
 * Lifecycle: listeners are added in connectedCallback and removed in
 * disconnectedCallback, so Theme Editor section replacement cannot accumulate
 * them and no document-level `shopify:section:load` handler is needed.
 */

const TAG = 'bstory-size-label';

function normalize(value) {
  return String(value == null ? '' : value).trim();
}

class BstorySizeLabel extends HTMLElement {
  connectedCallback() {
    this.mappings = this.readJSON('[data-bstory-size-mappings]');
    this.variants = this.readJSON('[data-bstory-product-variants]');
    this.valueEl = this.querySelector('[data-bstory-size-value]');
    this.optionIndex = parseInt(this.dataset.optionIndex, 10);
    this.form = this.dataset.formId
      ? document.getElementById(this.dataset.formId)
      : null;

    if (
      !this.valueEl ||
      !this.form ||
      Number.isNaN(this.optionIndex) ||
      this.optionIndex < 0
    ) {
      return;
    }

    this.onChange = () => this.schedule();
    // The master variant input lives inside the form and its `change` bubbles,
    // as do the option inputs'. One listener on the form covers both.
    this.form.addEventListener('change', this.onChange);

    this.sync();
  }

  disconnectedCallback() {
    if (this.form && this.onChange) {
      this.form.removeEventListener('change', this.onChange);
    }
    this.onChange = null;
  }

  readJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * An option input's `change` can arrive before the picker has written the new
   * id to the master input, so read on the next frame rather than inline. No
   * arbitrary timeout.
   */
  schedule() {
    queueMicrotask(() => requestAnimationFrame(() => this.sync()));
  }

  selectedVariantId() {
    const input = this.form.querySelector('input[name="id"]');
    return input ? normalize(input.value) : '';
  }

  /** Re-resolved on every sync — the variant is never cached from first paint. */
  currentOptionValue() {
    const id = this.selectedVariantId();
    if (!id) return null;

    for (let i = 0; i < this.variants.length; i += 1) {
      const variant = this.variants[i];
      if (variant && normalize(variant.id) === id) {
        return Array.isArray(variant.options)
          ? normalize(variant.options[this.optionIndex])
          : null;
      }
    }
    return null;
  }

  /** Exact match after trimming, case-insensitive for Latin values. */
  mapValue(raw) {
    const needle = raw.toLowerCase();

    for (let i = 0; i < this.mappings.length; i += 1) {
      const pair = this.mappings[i];
      const option = normalize(pair && pair.option);
      if (option && option.toLowerCase() === needle) {
        const display = normalize(pair.display);
        if (display) return display;
        break;
      }
    }

    return raw || normalize(this.dataset.fallback);
  }

  sync() {
    const raw = this.currentOptionValue();
    if (raw === null) return;

    const value = this.mapValue(raw);

    if (!value) {
      // Nothing usable for this variant — hide rather than render a prefix
      // with nothing after it.
      this.hidden = true;
      return;
    }

    this.hidden = false;
    if (this.valueEl.textContent !== value) this.valueEl.textContent = value;
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, BstorySizeLabel);
}
